import { apiFetch, API_BASE_URL, ensureCsrfToken } from './client';
import type { Topic } from './discover';
import type { Visibility } from './visibility';
import { ApiError, type ApiErrorBody } from './client';
import type { BlockResult, DashboardBlock } from './dashboards';

/** The ten kinds of block a story can hold. */
export type StoryBlockType =
  | 'title'
  | 'subtitle'
  | 'rich_text'
  | 'image'
  | 'dashboard'
  | 'chart'
  | 'kpi'
  | 'table'
  | 'quote'
  | 'divider';

export interface StoryBlockTypeInfo {
  key: StoryBlockType;
  name: string;
  description: string;
  is_text: boolean;
  is_embed: boolean;
  is_ornament: boolean;
}

export interface StoryBlock {
  id: string;
  type: StoryBlockType;
  content: {
    text?: string;
    attribution?: string;
    caption?: string;
    alt?: string;
    image?: string;
    dashboard?: string;
    block?: string;
  };
}

/** Where one version of a story stands. */
export type VersionStatus = 'DRAFT' | 'PENDING_REVIEW' | 'REJECTED' | 'PUBLISHED' | 'ARCHIVED';

/** Where a story stands: what a reader sees, not what its author is writing. */
export type PostStatus =
  'DRAFT' | 'PENDING_REVIEW' | 'REJECTED' | 'PUBLISHED' | 'UNPUBLISHED' | 'HIDDEN';

/** The last decision a moderator made about a story. Only its author sees it. */
export interface ModerationNote {
  decision: 'APPROVED' | 'REJECTED' | 'HIDDEN' | 'UNHIDDEN';
  label: string;
  reason: string;
  version: number;
  decided_at: string;
}

/** One version of a story, without its body. */
export interface StoryVersion {
  number: number;
  status: VersionStatus;
  title: string;
  subtitle: string;
  block_count: number;
  dashboards: string[];
  created_at: string;
  updated_at: string;
  submitted_at: string | null;
  published_at: string | null;
  archived_at: string | null;
}

/** One version of a story, with everything it says. */
export interface StoryVersionDetail extends StoryVersion {
  blocks: StoryBlock[];
}

export interface Post {
  id: string;
  title: string;
  subtitle: string;
  status: PostStatus;
  moderation: ModerationNote | null;
  blocks: StoryBlock[];
  block_count: number;
  dashboards: string[];
  /** The version this representation is of. */
  version: StoryVersion | null;
  /**
   * What a reader is served, what is being written, and what is waiting for a
   * moderator. Any of them may be absent — and a story can be published *and*
   * have its next version in review.
   */
  published_version: number | null;
  draft_version: number | null;
  review_version: number | null;
  version_count: number;
  /** Who may see it, and whether the caller is the one who wrote it. */
  visibility: Visibility;
  is_mine: boolean;
  /** Who wrote it, what it is about, and how it is doing (Phase 19). */
  author: { handle: string; display_name: string };
  category: Topic | null;
  tags: Topic[];
  is_following_author: boolean;
  /** Whether *you* kept it. Private: never a count, never the author's business. */
  is_favorite: boolean;
  /** What other people said, in public (Phase 18) — the counterpart of the above. */
  like_count: number;
  is_liked: boolean;
  /** Whether the backend would accept a like from you. Never inferred here. */
  can_like: boolean;
  comment_count: number;
  view_count: number;
  first_published_at: string | null;
  featured_at: string | null;
  created_at: string;
  updated_at: string;
  /** Present after a save: what would leave a reader guessing. */
  reading_problems?: string[];
}

export type PostSummary = Omit<Post, 'blocks'>;

/** Which version to read: what a reader sees, the draft, or one by number. */
export type VersionAsked = 'published' | 'draft' | 'latest' | number;

/** One block of a rendered story: prose as it stands, a figure with its answer. */
export interface RenderedStoryBlock {
  id: string;
  type: StoryBlockType;
  /** For chart, kpi and table embeds. */
  block?: DashboardBlock;
  state?: BlockResult;
  /** For a whole-dashboard embed, in reading order and without its controls. */
  blocks?: { block: DashboardBlock; state?: BlockResult }[];
  error?: { code: string; message: string };
}

export interface RenderedStory {
  id: string;
  version: number;
  blocks: RenderedStoryBlock[];
}

export interface StoryImage {
  id: string;
  content_type: string;
  byte_size: number;
  url: string;
}

export async function listStoryBlockTypes(): Promise<StoryBlockTypeInfo[]> {
  const body = await apiFetch<{ block_types: StoryBlockTypeInfo[] }>('/posts/block-types');
  return body.block_types;
}

export async function listPosts(): Promise<PostSummary[]> {
  const body = await apiFetch<{ results: PostSummary[] }>('/posts');
  return body.results;
}

export async function getPost(id: string, version?: VersionAsked): Promise<Post> {
  return apiFetch<Post>(`/posts/${id}${version === undefined ? '' : `?version=${version}`}`);
}

export async function createPost(input: { title: string; subtitle?: string }): Promise<Post> {
  return apiFetch<Post>('/posts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function updatePost(
  id: string,
  changes: { title?: string; subtitle?: string },
): Promise<Post> {
  return apiFetch<Post>(`/posts/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(changes),
  });
}

export async function savePostBlocks(id: string, blocks: StoryBlock[]): Promise<Post> {
  return apiFetch<Post>(`/posts/${id}/blocks`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ blocks }),
  });
}

export async function renderPost(id: string, version?: VersionAsked): Promise<RenderedStory> {
  const query = version === undefined ? '' : `?version=${version}`;
  return apiFetch<RenderedStory>(`/posts/${id}/render${query}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
}

export async function deletePost(id: string): Promise<void> {
  return apiFetch<void>(`/posts/${id}`, { method: 'DELETE' });
}

/**
 * The life of a story (Phase 13).
 *
 * A published version is never written to: `openDraft` forks the next one, and
 * everything the editor saves lands there while the published version keeps
 * being served.
 */
export async function openDraft(id: string): Promise<Post> {
  return act(id, 'draft');
}

/** Hand the draft over for publication. */
export async function submitPost(id: string): Promise<Post> {
  return act(id, 'submit');
}

/** Take a version back out of review, to keep writing it. */
export async function withdrawPost(id: string): Promise<Post> {
  return act(id, 'withdraw');
}

/** Take a published story down. What was published is archived, not deleted. */
export async function unpublishPost(id: string): Promise<Post> {
  return act(id, 'unpublish');
}

export async function listVersions(id: string): Promise<StoryVersion[]> {
  const body = await apiFetch<{ versions: StoryVersion[] }>(`/posts/${id}/versions`);
  return body.versions;
}

export async function getVersion(id: string, number: number): Promise<StoryVersionDetail> {
  return apiFetch<StoryVersionDetail>(`/posts/${id}/versions/${number}`);
}

/** Bring an old version's words back into the draft. */
export async function restoreVersion(id: string, number: number): Promise<Post> {
  return act(id, `versions/${number}/restore`);
}

function act(id: string, path: string): Promise<Post> {
  return apiFetch<Post>(`/posts/${id}/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
}

/**
 * Keep a story to come back to, or stop (Phase 23).
 *
 * Private by design: nothing about it reaches the author, and nothing counts it.
 */
export async function keepPost(id: string): Promise<{ is_favorite: boolean }> {
  return apiFetch<{ is_favorite: boolean }>(`/posts/${id}/favorite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
}

export async function forgetPost(id: string): Promise<{ is_favorite: boolean }> {
  return apiFetch<{ is_favorite: boolean }>(`/posts/${id}/favorite`, { method: 'DELETE' });
}

/** Shelve a story: its category, its tags. Metadata — never a new version. */
export async function setTopics(
  id: string,
  topics: { category?: string; tags?: string[] },
): Promise<Post> {
  return apiFetch<Post>(`/posts/${id}/topics`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(topics),
  });
}

/** Where a story's image is served from. Behind the API, like the story itself. */
export function imageUrl(postId: string, imageId: string): string {
  return `${API_BASE_URL}/posts/${postId}/images/${imageId}`;
}

/**
 * Upload an image for a story.
 *
 * `fetch` with FormData rather than `apiFetch`: the browser sets the multipart
 * boundary itself, and a Content-Type we chose would break the upload.
 */
export async function uploadStoryImage(postId: string, file: File): Promise<StoryImage> {
  const token = await ensureCsrfToken();
  const form = new FormData();
  form.append('file', file);

  const response = await fetch(`${API_BASE_URL}/posts/${postId}/images`, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json', 'X-CSRFToken': token ?? '' },
    body: form,
  });

  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const error = (payload as ApiErrorBody | null)?.error;
    throw new ApiError(
      response.status,
      error ?? { code: 'UNEXPECTED_ERROR', message: 'The image could not be uploaded.' },
    );
  }
  return payload as StoryImage;
}
